import { Connect, ConnectInstaller } from "@ibm-aspera/connect-sdk-js";
import {
  AllTransfersInfo,
  ConnectClientType,
  ConnectError,
  StartTransferOutput,
  TransferSpec,
} from "@ibm-aspera/connect-sdk-js/dist/esm/core/types";
import { Box, Button, CircularProgress, Stack, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import { cacheService, fileService } from "../../../Core/Api/services";
import { DbFileInfos } from "../../../Core/Api/Types/db-file-infos";
import { FileCategory } from "../../../Core/Api/Types/file-category";
import { StorageKind } from "../../../Core/Api/Types/storage-kind";
import { FileStatus } from "../../../Core/Api/Types/file-status";
import { CreateAsperaTransferRequest } from "../../../Core/Api/Types/create-aspera-transfer-request";
import { FileTransferInitiator } from "../../../Core/Api/Types/file-transfer-initiator";
import { AsperaTransferSpecBasic, TransferSpecDTO } from "../../../Core/Api/Types/aspera-transfer-spec-basic";
import SwitchPlayer from "./switch-player";
import { ProgressBar } from "../../../Components/Misc/DropFileZone/drop-file-zone";
import { useTranslation } from "react-i18next";

export type SwitchPlayerWrapperProps = {
  files: DbFileInfos[];
  setSwitchData: any;
};

const SwitchPlayerWrapper: React.FC<SwitchPlayerWrapperProps> = (props) => {
  const { t } = useTranslation();
  const { files, setSwitchData } = props;

  // Hooks
  const theme = useTheme();
  const enqueueCustomSnackbar = useCustomSnackbar();

  // State
  const [connect, setConnect] = useState<ConnectClientType>(new Connect({}));
  const [transferId, setTransferId] = useState<string>("");
  const [linkToSwitchPath, setLinkToSwitchPath] = useState<string>("");
  const [fileTodownload, setFileToDownload] = useState<DbFileInfos | undefined>();
  const [transferSpec, setTransferSpec] = useState<AsperaTransferSpecBasic | undefined>();
  const [transferLink, setTransferLink] = useState<string | undefined>();
  const connectInstaller = useMemo(() => new ConnectInstaller(), []);
  const [percentage, setPercentage] = useState<number>(0);
  const [connectStatus, setConnectStatus] = useState<string>("");
  const [transferStatus, setTransferStatus] = useState<string>("");
  const [needToDownloadFile, setNeedToDownloadFile] = useState<boolean>(false);

  // Memos
  const connectTextColorStatus: string[] = useMemo(() => {
    const defaultValue = ["Connect not initialized", theme.palette.error.main];
    if (!connect) return defaultValue;
    switch (connectStatus) {
      case "INITIALIZING":
        return ["Aspera Connect is Initializing", theme.palette.primary.main];
      case "RUNNING":
        return ["Connect is Running", theme.palette.success.main];
      case "FAILED":
      case "DEGRADED":
      case "OUTDATED":
      case "STOPPED":
        return ["Error with Aspera Connect", theme.palette.error.main];
      case "RETRYING":
      case "WAITING":
        return ["Waiting for connection", theme.palette.warning.main];
      case "EXTENSION_INSTALL":
        return ["Extension installation", theme.palette.primary.main];
      default:
        return defaultValue;
    }
  }, [connectStatus]);

  const transferStatusColorText = useMemo(() => {
    switch (transferStatus) {
      case "willretry":
        return ["Retry after 30sec", theme.palette.warning.main];
      case "queued":
        return ["In Queue", theme.palette.warning.main];
      case "initiating":
        return ["Initiating", theme.palette.primary.main];
      case "removed":
        return ["Removed", theme.palette.error.main];
      case "cancelled":
        return ["Cancelled", theme.palette.error.main];
      case "failed":
        return ["Download Failed", theme.palette.error.main];
      case "running":
        return [`In progress: ${percentage}%`, theme.palette.primary.main];
      case "completed":
        return ["Completed", theme.palette.success.main];
      default:
        return transferStatus;
    }
  }, [transferStatus, percentage]);

  // functions and callbacks
  const findAllowFile = () => {
    const allowedFiles = files.filter(
      (f) =>
        f.storageInfos?.kind === StorageKind.StandardAndWork &&
        f.category === FileCategory.Native &&
        f.status === FileStatus.Online
    );
    if (!allowedFiles || allowedFiles.length === 0) {
      enqueueCustomSnackbar(t("alert:error.noFileForPlayback"), "error");
      return;
    }

    enqueueCustomSnackbar(t("alert:primary.availableFileToDownload", { name: allowedFiles[0].name }), "primary");
    setFileToDownload(allowedFiles[0]);
  };

  const initConnect = () => {
    let localConnect: ConnectClientType = new Connect({});
    localConnect.addEventListener(Connect.EVENT.STATUS, connectStatusEventListener);
    localConnect.initSession();
    setConnect(localConnect);
  };

  const connectStatusEventListener = (eventType: string, data: string) => {
    setConnectStatus(data);
    if (eventType === Connect.EVENT.STATUS && data === Connect.STATUS.INITIALIZING) {
      connectInstaller.showLaunching();
    } else if (eventType === Connect.EVENT.STATUS && data === Connect.STATUS.EXTENSION_INSTALL) {
      connectInstaller.showExtensionInstall();
    } else if (eventType === Connect.EVENT.STATUS && data === Connect.STATUS.FAILED) {
      connectInstaller.showDownload();
    } else if (eventType === Connect.EVENT.STATUS && data === Connect.STATUS.OUTDATED) {
      connectInstaller.showUpdate();
    } else if (eventType === Connect.EVENT.STATUS && data === Connect.STATUS.RUNNING) {
      connectInstaller.connected();
    }
  };

  const handleGetAllTransferSuccess = (data: AllTransfersInfo) => {
    const foundTransferIndex = data.transfers.findLastIndex((x) => x.title === fileTodownload!.name);

    if (foundTransferIndex > -1) setTransferId(data.transfers[foundTransferIndex].uuid);
    else {
      createTransferLink(fileTodownload!.id);
    }
  };

  const createTransferLink = (fileId: number) => {
    fileService
      .CreateTransferDownloadLink(
        new CreateAsperaTransferRequest({
          fileIds: [fileId],
          initiator: FileTransferInitiator.AsperaConnectFasp,
          noEmail: true,
          expireDays: 1,
          emails: [],
        })
      )
      .then((res) => {
        if (res.success && res.message) {
          enqueueCustomSnackbar(t("alert:primary.transferDownloadLinkCreated"), "primary");
          setTransferLink(res.message);
        } else {
          enqueueCustomSnackbar(res.message || t("alert:error.requestingDownloadLink"), "error");
        }
      });
  };

  const handleStartTransferSuccess = (response: StartTransferOutput) => {
    enqueueCustomSnackbar(
      t("alert:primary.transferSuccessStart", {
        destination: response.transfer_specs[0].transfer_spec.destination_root,
      }),
      "primary"
    );
    setTransferId(response.transfer_specs[0].uuid);
  };

  const handleStartTransferFailed = (error: ConnectError) => {
    enqueueCustomSnackbar(t("alert:error.transferFailedToStart", { message: error.error.user_message }), "error");
  };

  const handleSuccessTransferRequest = (data) => {
    setPercentage(parseInt((data.transfer_info.percentage * 100).toFixed(0)));
    setTransferStatus(data.transfer_info.status);
    if (data.transfer_info.status === "completed") {
      const foundPath = data.transfer_info.explorer_path;
      setLinkToSwitchPath(foundPath.replace("\\", "/"));
      cacheService.SetDowloadedFilesPath(fileTodownload!.id, foundPath.replace("\\", "/"));
    }
  };
  const handleFailedTransferRequest = (error: ConnectError) => {
    enqueueCustomSnackbar(t("alert:error.transferRequestFailed", { message: error.error.user_message }), "error");
  };

  // effect chain
  useEffect(() => {
    findAllowFile();
  }, []);

  useEffect(() => {
    if (!fileTodownload) return;
    const cachedPath = cacheService.GetfilePath(fileTodownload.id);

    if (cachedPath) {
      enqueueCustomSnackbar(t("alert:primary.pathFoundInCache", { cachedPath }), "primary");
      setLinkToSwitchPath(cachedPath);
      setPercentage(100);
    } else {
      enqueueCustomSnackbar(t("alert:primary.noPathFoundInCache"), "primary");
      setNeedToDownloadFile(true);
      initConnect();
    }
  }, [fileTodownload]);

  useEffect(() => {
    if (connectStatus === "RUNNING") {
      connect.getAllTransfers({
        success: handleGetAllTransferSuccess,
        error: (e) => {
          enqueueCustomSnackbar(e.error.user_message, "error");
        },
      });
    }
  }, [connectStatus]);

  useEffect(() => {
    if (!transferLink) return;

    fileService.DownloadAsperaFile(transferLink).then((res) => {
      if (res.success && res.transferSpec) {
        setTransferSpec(res.transferSpec);
        enqueueCustomSnackbar(t("alert:primary.transferSpecificationsReceived"), "primary");
      } else {
        enqueueCustomSnackbar(res.message || t("alert:error.requestingTransferSpecifications"), "error");
      }
    });
  }, [transferLink]);

  useEffect(() => {
    if (!transferSpec || connectStatus !== "RUNNING") return;

    connect.startTransfer(
      TransferSpecDTO(transferSpec) as TransferSpec,
      { allow_dialogs: true },
      {
        success: handleStartTransferSuccess,
        error: handleStartTransferFailed,
      }
    );
  }, [transferSpec, connectStatus]);

  useEffect(() => {
    if (transferId === "") return;

    const transferCheck = setInterval(() => {
      connect.getTransfer(transferId, {
        success: handleSuccessTransferRequest,
        error: handleFailedTransferRequest,
      });
    }, 1000);

    return () => {
      clearInterval(transferCheck);
    };
  }, [transferId]);

  if (!fileTodownload)
    return (
      <Box flexDirection={"column"} height={"390px"} display={"flex"} justifyContent={"center"} alignItems={"center"}>
        <Typography variant="h6"> {t("page:error.noFileForSwitchLink")}</Typography>
      </Box>
    );

  if ((!linkToSwitchPath || percentage === 0 || percentage < 100) && needToDownloadFile)
    return (
      <Box
        width={"100%"}
        flexDirection={"column"}
        height={"390px"}
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
      >
        <Box
          sx={{
            backgroundColor: theme.palette.background.alphaHover,
            border: `1px solid ${theme.palette.backgroundAlpha.borderFocus}`,
          }}
          width={"320px"}
          flexDirection={"column"}
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
          pt={1}
          pb={1}
          pr={2}
          pl={2}
        >
          <Typography sx={{ mb: 2 }} variant="h6">
            {t("page:body.downloadInfos")}
          </Typography>
          <Stack display={"flex"} width={"100%"} justifyContent={"space-between"} mb={1} spacing={2} direction={"row"}>
            <Typography lineHeight={"13px"} color={"text.tier"} variant="body2">
              {t("page:body.connectStatus")}
            </Typography>
            <Stack spacing={1} direction={"row"}>
              <Typography lineHeight={"13px"} variant="body2">
                {connectTextColorStatus[0]}
              </Typography>
              <Box
                component="span"
                sx={{ bgcolor: connectTextColorStatus[1], width: 13, height: 13, borderRadius: "50%" }}
              />
            </Stack>
          </Stack>
          {transferStatus !== "" && (
            <Stack mb={2} spacing={2} width={"100%"} justifyContent={"space-between"} direction={"row"}>
              <Typography lineHeight={"13px"} color={"text.tier"} variant="body2">
                {t("page:body.transferStatus")}
              </Typography>
              <Stack spacing={1} direction={"row"}>
                <Typography lineHeight={"13px"} variant="body2">
                  {transferStatusColorText[0]}
                </Typography>
                <Box
                  component="span"
                  sx={{ bgcolor: transferStatusColorText[1], width: 13, height: 13, borderRadius: "50%" }}
                />
              </Stack>
            </Stack>
          )}
          {transferStatus === "running" ? (
            <ProgressBar progress={percentage} />
          ) : transferStatus === "removed" || transferStatus === "cancelled" || transferStatus === "failed" ? (
            <Button
              variant="outlined"
              color="primary"
              onClick={(e) => {
                setTransferSpec(undefined);
                createTransferLink(fileTodownload.id);
              }}
            >
              {t("button:tryAgain")}
            </Button>
          ) : (
            <CircularProgress size={25} />
          )}
        </Box>
      </Box>
    );

  if (!linkToSwitchPath && !needToDownloadFile)
    return (
      <Box
        width={"100%"}
        flexDirection={"column"}
        height={"390px"}
        display={"flex"}
        justifyContent={"center"}
        alignItems={"center"}
      >
        <Typography variant="body1">{t("button:lookingForFileToLink")}</Typography>
      </Box>
    );

  return <SwitchPlayer path={linkToSwitchPath} setSwitchData={setSwitchData} />;
};

export default SwitchPlayerWrapper;
